/**
 * Settings an account keeps OFF its public users/{uid} document.
 *
 * users/{uid} is world-readable and listable (/search lists it signed out), and
 * the browser's time zone and the display currency place a person, even a
 * seller whose location switch is off (2026-10-26 audit, item 10). They live in
 * the owner-only users/{uid}/settings/locale instead; firestore.rules refuses
 * them on the public document. The auth context reads that doc and merges it
 * into `user.settings`, so readers are unchanged, and updateUser sends these
 * keys there (splitPrivateSettings).
 *
 * MIRRORS PRIVATE_SETTINGS_KEYS in functions/src/lib/private-user-fields.ts,
 * which moves any left on a public document; the test compares the two.
 */

export const PRIVATE_SETTINGS_KEYS = ['currency', 'timezone'] as const;

export type PrivateSettingsKey = (typeof PRIVATE_SETTINGS_KEYS)[number];

/** users/{uid}/settings/{these}. */
export const LOCALE_SETTINGS_DOC = 'locale';
export const MESSAGING_SETTINGS_DOC = 'messaging';

const isPrivateKey = (key: string): key is PrivateSettingsKey =>
  (PRIVATE_SETTINGS_KEYS as readonly string[]).includes(key);

/**
 * A settings patch, split into what goes on the public document and what goes
 * to settings/locale.
 *
 * @param patch The patch updateUser was given, or null.
 */
export function splitPrivateSettings<T extends Record<string, unknown>>(patch: T | null): {
  publicPatch: Partial<T> | null;
  privatePatch: Partial<Record<PrivateSettingsKey, unknown>> | null;
} {
  if (!patch) return { publicPatch: null, privatePatch: null };
  const publicPatch: Record<string, unknown> = {};
  const privatePatch: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(patch)) {
    if (isPrivateKey(key)) privatePatch[key] = value;
    else publicPatch[key] = value;
  }
  return {
    publicPatch: Object.keys(publicPatch).length > 0 ? (publicPatch as Partial<T>) : null,
    privatePatch: Object.keys(privatePatch).length > 0 ? privatePatch : null,
  };
}

/**
 * The settings an account sees: its public settings, with the owner-only
 * locale values on top. A legacy value still on the public document shows
 * until the server moves it, unless the locale doc already holds a newer one.
 *
 * @param settings users/{uid}.settings.
 * @param locale users/{uid}/settings/locale, or null while it has not loaded.
 */
export function mergePrivateSettings<T extends object>(settings: T, locale: Record<string, unknown> | null): T {
  if (!locale) return settings;
  const out = { ...settings } as Record<string, unknown>;
  for (const key of PRIVATE_SETTINGS_KEYS) {
    const value = locale[key];
    if (typeof value === 'string' && value !== '') out[key] = value;
  }
  return out as T;
}
